import type * as Dialog from '@radix-ui/react-dialog';
import type {
  AllStyledProps,
  CommonStyledProps,
  CSSProperties,
  CSSStyledProps,
} from '@sds-system/types';
import type { ReactNode } from 'react';

// root styled component에 대한 타입
export interface ModalRootStyledOptions
  extends Omit<AllStyledProps, keyof CommonStyledProps>,
    CSSStyledProps {
  height?: CSSProperties['height'][] | CSSProperties['height'];
  width?: CSSProperties['width'][] | CSSProperties['width'];
}
export interface ModalRootInternalStyledOptions {
  borderRadius?: CSSProperties['borderRadius'];
  maxHeight?: CSSProperties['maxHeight'][];
  maxWidth?: CSSProperties['maxWidth'][];
  minHeight?: CSSProperties['minHeight'][];
  minWidth?: CSSProperties['minWidth'][];
  padding?: CSSProperties['padding'];
}
export interface ModalRootStateOptions extends Pick<ModalOptions, 'isResponsive'> {}
export type ModalRootStyledComponentProps = ModalRootStyledOptions &
  ModalRootInternalStyledOptions &
  ModalRootStateOptions;

// title styled component에 대한 타입
export interface ModalTitleWrapperStyledOptions extends AllStyledProps, CSSStyledProps {}
export interface ModalTitleWrapperStateOptions extends Pick<ModalOptions, 'isResponsive'> {}
export type ModalTitleWrapperComponentProps = ModalTitleWrapperStyledOptions &
  ModalTitleWrapperStateOptions;

// content styled component에 대한 타입
export interface ModalContentWrapperStyledOptions extends AllStyledProps, CSSStyledProps {}
export interface ModalContentWrapperStateOptions extends Pick<ModalOptions, 'isResponsive'> {}
export type ModalContentStyledComponentProps = ModalContentWrapperStyledOptions &
  ModalContentWrapperStateOptions;
export interface ModalOverlayStyledComponentProps {
  zIndex?: number;
}

// 외부에서 받을 styled props를 묶는 타입
export interface ModalStyledComponentsOptions {
  styledComponents?: {
    content?: ModalContentWrapperStyledOptions;
    overlay?: Omit<ModalRootStyledOptions, 'width' | 'height'>;
    root?: ModalRootStyledOptions;
    title?: ModalTitleWrapperStyledOptions;
  };
}

type DialogRootProps = Omit<Dialog.DialogProps, 'open' | 'onOpenChange'>;
type DialogPortalProps = Omit<Dialog.DialogPortalProps, 'children' | 'forceMount'>;
export interface ModalOptions {
  children?: ReactNode;
  exitHandler: () => void;
  hasCloseButton?: boolean;
  isDuplication?: boolean;
  isOpen: boolean;
  isResponsive?: boolean;
  title?: string | ReactNode;
}

export const modalOptionsKey: (
  | keyof ModalOptions
  | keyof DialogRootProps
  | keyof DialogPortalProps
)[] = [
  'exitHandler',
  'hasCloseButton',
  'isOpen',
  'title',
  'container',
  'defaultOpen',
  'isResponsive',
  'isDuplication',
] as const;

export interface ModalInternalProps extends Pick<ModalOptions, 'isResponsive' | 'hasCloseButton'> {
  styledComponents?: {
    content?: ModalContentWrapperStyledOptions;
    root?: ModalRootInternalStyledOptions;
  };
}
export interface ModalProps
  extends ModalOptions,
    DialogRootProps,
    DialogPortalProps,
    ModalStyledComponentsOptions,
    Pick<ModalRootStyledOptions, 'width' | 'height'> {}
